// BetMathBuilder.tsx - the manual bet-math tier (locked dx5). The second input tier: after the user
// picks Raise, they BUILD the dollar number themselves. This is the load-bearing do-your-own-math
// surface (North Star §4.2) - so NOTHING here pre-fills the amount:
//   - the "Raise to $__" input starts EMPTY (placeholder only);
//   - the pot / to-call / their-bet facts are echoed for the arithmetic, never summed for you;
//   - the quick-size chips fill the field only when the USER taps one (their action, not an auto-fill),
//     and stay togglable.
// Confirm reads whatever the user typed. Ported from the mockup #dg-betmath / openBet / commitAction.
import { useState } from 'react';
import type { BetMathDef } from './data';

export interface BetMathBuilderProps {
  bet: BetMathDef;
  onCancel: () => void;
  /** the user's built number (raw string, exactly as typed/tapped). */
  onConfirm: (amount: string) => void;
}

export function BetMathBuilder({ bet, onCancel, onConfirm }: BetMathBuilderProps) {
  // starts empty - the app never fills a number for the user.
  const [amount, setAmount] = useState('');
  const [activeChip, setActiveChip] = useState<string | null>(null);

  const digitsOnly = (s: string) => s.replace(/[^0-9.]/g, '');

  return (
    <div className="betmath">
      <div className="bmhead">
        Build the number yourself
        <s>Nothing is filled in. You do the math.</s>
      </div>

      <div className="bmgrid">
        <div className="bmfact">
          <span>Pot</span>
          <b>{bet.pot}</b>
        </div>
        <div className="bmfact">
          <span>{bet.callLabel}</span>
          <b>{bet.call}</b>
        </div>
        <div className="bmfact">
          <span>Their bet</span>
          <b>{bet.vbet}</b>
        </div>
      </div>

      <div className="bmquick">
        {bet.quick.map((q) => (
          <button
            key={q.amount}
            type="button"
            className={activeChip === q.amount ? 'on' : ''}
            onClick={() => {
              setActiveChip(q.amount);
              setAmount(digitsOnly(q.amount));
            }}
          >
            {q.amount}
            <s>{q.ratio}</s>
          </button>
        ))}
      </div>

      <div className="bminput">
        <span className="pfx">{bet.pfx}</span>
        <span className="cur">$</span>
        <input
          inputMode="numeric"
          autoComplete="off"
          placeholder="enter your number"
          value={amount}
          onChange={(e) => {
            setAmount(digitsOnly(e.target.value));
            setActiveChip(null);
          }}
        />
      </div>

      <div className="bmsub">{bet.sub}</div>

      <div className="bmrow">
        <button type="button" className="cta ghost" onClick={onCancel}>
          Back
        </button>
        <button type="button" className="cta" disabled={!amount} onClick={() => onConfirm(amount)}>
          Confirm
        </button>
      </div>
    </div>
  );
}
